import { CONFIG } from '../config';
import { Laser } from '../entities/Laser';
import type { Player } from '../entities/Player';

interface RendererLike {
    getCanvasWidth(): number;
    getCanvasHeight(): number;
    isLandscape: boolean;
}

/**
 * 레이저 스폰 및 관리
 */
export class LaserSpawner {
    private lasers: Laser[] = [];
    private gameTime: number = 0;
    private timeSinceLastSpawn: number = 0;
    private spawnInterval: number = 8; // 8초마다 레이저
    private renderer: RendererLike | null = null;
    private enabled: boolean = false; // 첫 보스 처치 후 활성화

    setRenderer(renderer: RendererLike): void {
        this.renderer = renderer;
    }

    /**
     * 레이저 스폰 활성화 (첫 보스 처치 후 호출)
     */
    enable(): void {
        this.enabled = true;
        this.timeSinceLastSpawn = 0;
    }

    private getCanvasSize(): { width: number; height: number; isLandscape: boolean } {
        if (this.renderer) {
            return {
                width: this.renderer.getCanvasWidth(),
                height: this.renderer.getCanvasHeight(),
                isLandscape: this.renderer.isLandscape
            };
        }
        return {
            width: CONFIG.CANVAS_WIDTH,
            height: CONFIG.CANVAS_HEIGHT,
            isLandscape: false
        };
    }

    update(deltaTime: number, gameTime: number): void {
        this.gameTime = gameTime;

        // 첫 보스 처치 후 활성화된 경우에만 레이저 스폰
        if (this.enabled) {
            this.timeSinceLastSpawn += deltaTime;

            if (this.timeSinceLastSpawn >= this.spawnInterval) {
                this.spawnLaser();
                this.timeSinceLastSpawn = 0;

                // 시간이 지날수록 스폰 간격 감소 (최소 4초)
                this.spawnInterval = Math.max(4, 8 - gameTime / 60);
            }
        }

        // 레이저 업데이트
        for (let i = this.lasers.length - 1; i >= 0; i--) {
            this.lasers[i].update(deltaTime);
            if (!this.lasers[i].isAlive) {
                this.lasers.splice(i, 1);
            }
        }
    }

    spawnLaser(): void {
        const { width, height, isLandscape } = this.getCanvasSize();
        const margin = CONFIG.PLAY_AREA.MARGIN;

        // 화면 방향에 따라 레이저 방향 결정
        // 가로 모드: 가로 레이저 (오른쪽에서 왼쪽 방향의 위협)
        // 세로 모드: 세로 레이저 (위에서 아래 방향의 위협)
        const isHorizontal = isLandscape;

        let position: number;
        if (isHorizontal) {
            // 수평 레이저: 플레이 영역 내 랜덤 y 위치
            const minY = margin + 50;
            const maxY = height - margin - 50;
            position = minY + Math.random() * (maxY - minY);
        } else {
            // 수직 레이저: 플레이 영역 내 랜덤 x 위치
            const minX = margin + 50;
            const maxX = width - margin - 50;
            position = minX + Math.random() * (maxX - minX);
        }

        this.lasers.push(new Laser(isHorizontal, position, width, height));
    }

    /**
     * 플레이어와 충돌 체크
     */
    checkCollisions(player: Player): boolean {
        for (const laser of this.lasers) {
            if (laser.checkCollision(player)) {
                return true;
            }
        }
        return false;
    }

    /**
     * 경고 중인 레이저 개수
     */
    getWarningCount(): number {
        return this.lasers.filter(l => l.phase === 'warning').length;
    }

    /**
     * 활성화된 레이저 개수
     */
    getActiveCount(): number {
        return this.lasers.filter(l => l.phase === 'active').length;
    }

    render(ctx: CanvasRenderingContext2D): void {
        for (const laser of this.lasers) {
            laser.render(ctx);
        }
    }

    getLasers(): Laser[] {
        return this.lasers;
    }

    reset(): void {
        this.lasers = [];
        this.gameTime = 0;
        this.timeSinceLastSpawn = 0;
        this.spawnInterval = 8;
        this.enabled = false;
    }

    /**
     * 화면 방향 전환 시 위치 변환
     */
    transformPositionsOnOrientationChange(wasLandscape: boolean, isLandscape: boolean): void {
        const oldWidth = wasLandscape ? CONFIG.CANVAS_WIDTH_LANDSCAPE : CONFIG.CANVAS_WIDTH;
        const oldHeight = wasLandscape ? CONFIG.CANVAS_HEIGHT_LANDSCAPE : CONFIG.CANVAS_HEIGHT;
        const newWidth = isLandscape ? CONFIG.CANVAS_WIDTH_LANDSCAPE : CONFIG.CANVAS_WIDTH;
        const newHeight = isLandscape ? CONFIG.CANVAS_HEIGHT_LANDSCAPE : CONFIG.CANVAS_HEIGHT;

        for (const laser of this.lasers) {
            if (laser.isHorizontal) {
                const normalizedY = laser.position / oldHeight;
                if (wasLandscape && !isLandscape) {
                    laser.position = normalizedY * newHeight;
                } else {
                    laser.position = normalizedY * newHeight;
                }
            } else {
                const normalizedX = laser.position / oldWidth;
                if (wasLandscape && !isLandscape) {
                    laser.position = normalizedX * newWidth;
                } else {
                    laser.position = normalizedX * newWidth;
                }
            }
        }
    }
}
